import React from "react";
import DefaultErrorFallback from "./DefaultErrorFallback";

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  errorId: string;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<{ error: Error | null; errorInfo: React.ErrorInfo | null; errorId: string }>;
  onError?: (error: Error, errorInfo: React.ErrorInfo, errorId: string) => void;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorId: ''
    };
  }

  // if an error happened, set the state to true
  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    console.error('ErrorBoundary caught an error:', error);
    return { 
      hasError: true, 
      error,
      errorId: `error-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Update state with error info
    this.setState({ errorInfo });

    // Log detailed error information
    console.group(`🚨 Error Boundary Error (${this.state.errorId})`);
    console.error('Error:', error);
    console.error('Error name:', error.name);
    console.error('Error message:', error.message);
    console.error('Error stack:', error.stack);
    console.error('Component stack:', errorInfo.componentStack);
    console.error('Error info:', errorInfo);
    console.groupEnd();

    // Send error to external service if provided
    if (this.props.onError) {
      try {
        this.props.onError(error, errorInfo, this.state.errorId);
      } catch (reportingError) {
        console.error('Failed to report error:', reportingError);
      }
    }

    // Additional production logging
    this.logErrorToServer(error, errorInfo);
  }

  private logErrorToServer = async (error: Error, errorInfo: React.ErrorInfo) => {
    try {
      const errorData = {
        errorId: this.state.errorId,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
        error: {
          name: error.name,
          message: error.message,
          stack: error.stack,
        },
        componentStack: errorInfo.componentStack,
        // Add any additional context you want to capture
        context: {
          // You can add user info, app state, etc.
        }
      };

      // Send to your error reporting service
      // Example: await fetch('/api/errors', { method: 'POST', body: JSON.stringify(errorData) });
      console.log('Error data for reporting:', errorData);
    } catch (loggingError) {
      console.error('Failed to log error to server:', loggingError);
    }
  };

  render() {
    // if error happened, return a fallback component
    if (this.state.hasError) {
      const FallbackComponent = this.props.fallback || DefaultErrorFallback;
      return (
        <FallbackComponent 
          error={this.state.error} 
          errorInfo={this.state.errorInfo} 
          errorId={this.state.errorId}
        />
      );
    }

    return this.props.children;
  }
}

